import { useState, useEffect, useCallback } from 'react';

export interface ReadingProgress {
  bookId: string;
  currentChapterUrl: string;
  currentChapterIndex: number;
  completedChapters: string[];
  lastReadAt: string;
  scrollPosition?: number;
}

export interface BookReadingProgress {
  [bookId: string]: ReadingProgress;
}

const READING_PROGRESS_KEY = 'memory_land_reading_progress';

export const useReadingProgress = () => {
  const [progress, setProgress] = useState<BookReadingProgress>({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const savedProgress = localStorage.getItem(READING_PROGRESS_KEY);
      console.log('🔍 Debug - Loading progress from localStorage:', savedProgress);
      if (savedProgress) {
        const parsed = JSON.parse(savedProgress);
        console.log('🔍 Debug - Parsed progress:', parsed);
        setProgress(parsed);
      }
    } catch (error) {
      console.error('Error loading reading progress:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save progress to localStorage whenever it changes
  const saveProgress = useCallback((newProgress: BookReadingProgress) => {
    try {
      localStorage.setItem(READING_PROGRESS_KEY, JSON.stringify(newProgress));
      setProgress(newProgress);
    } catch (error) {
      console.error('Error saving reading progress:', error);
    }
  }, []);

  // Update progress for a specific book
  const updateBookProgress = useCallback((
    bookId: string,
    chapterUrl: string,
    chapterIndex: number,
    scrollPosition?: number
  ) => {
    const newProgress = { ...progress };
    
    if (!newProgress[bookId]) {
      newProgress[bookId] = {
        bookId,
        currentChapterUrl: chapterUrl,
        currentChapterIndex: chapterIndex,
        completedChapters: [],
        lastReadAt: new Date().toISOString(),
        scrollPosition
      };
    } else {
      newProgress[bookId] = {
        ...newProgress[bookId],
        currentChapterUrl: chapterUrl,
        currentChapterIndex: chapterIndex,
        lastReadAt: new Date().toISOString(),
        scrollPosition
      };
    }

    saveProgress(newProgress);
  }, [progress, saveProgress]);

  // Mark a chapter as completed
  const markChapterCompleted = useCallback((bookId: string, chapterUrl: string) => {
    const newProgress = { ...progress };
    
    if (!newProgress[bookId]) {
      newProgress[bookId] = {
        bookId,
        currentChapterUrl: chapterUrl,
        currentChapterIndex: 0,
        completedChapters: [chapterUrl],
        lastReadAt: new Date().toISOString()
      };
    } else {
      const completedChapters = [...newProgress[bookId].completedChapters];
      if (!completedChapters.includes(chapterUrl)) {
        completedChapters.push(chapterUrl);
      }
      
      newProgress[bookId] = {
        ...newProgress[bookId],
        completedChapters,
        lastReadAt: new Date().toISOString()
      };
    }

    saveProgress(newProgress);
  }, [progress, saveProgress]);

  // Get progress for a specific book
  const getBookProgress = useCallback((bookId: string): ReadingProgress | null => {
    const bookProgress = progress[bookId] || null;
    console.log('🔍 Debug - getBookProgress called:', {
      bookId,
      bookProgress,
      allProgress: progress
    });
    return bookProgress;
  }, [progress]);

  // Check if a chapter is completed
  const isChapterCompleted = useCallback((bookId: string, chapterUrl: string): boolean => {
    const bookProgress = progress[bookId];
    return bookProgress ? bookProgress.completedChapters.includes(chapterUrl) : false;
  }, [progress]);

  // Get reading progress percentage for a book
  const getReadingPercentage = useCallback((bookId: string, totalChapters: number): number => {
    const bookProgress = progress[bookId];
    if (!bookProgress || totalChapters === 0) return 0;
    
    return Math.round((bookProgress.completedChapters.length / totalChapters) * 100);
  }, [progress]);

  // Clear progress for a book
  const clearBookProgress = useCallback((bookId: string) => {
    const newProgress = { ...progress };
    delete newProgress[bookId];
    saveProgress(newProgress);
  }, [progress, saveProgress]);

  // Get all books with progress
  const getBooksWithProgress = useCallback((): string[] => {
    return Object.keys(progress);
  }, [progress]);

  return {
    progress,
    isLoaded,
    updateBookProgress,
    markChapterCompleted,
    getBookProgress,
    isChapterCompleted,
    getReadingPercentage,
    clearBookProgress,
    getBooksWithProgress
  };
};
